import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref } from 'vue'

import { cloneDeep, sampleSize } from 'lodash'

import api from '@app/insights/api'
import serialize from '@app/insights/utils/serialize'
import { periodSelectionTypes } from '@app/insights/types'
import I18n from '@app/adapters/I18n'
import { SfLog } from '@app/adapters/logger'
import { useFiltersStore } from '@app/insights/store/filters'
import { useInsightsRootStore } from '@app/insights/store/insights-root'
import { useCompaniesStore } from '@app/insights/store/companies'
import { useSegmentsStore } from '@app/insights/store/segments'
import { useColumnsStore } from '@app/insights/store/columns'
import type { ActiveFilter } from '@app/insights/store/filters/types'

export type AssistantRole = 'user' | 'assistant'

export interface AssistantMessage {
  id: string
  role: AssistantRole
  text: string
  // Suggested follow-up prompts. Only ever populated on assistant messages,
  // and only the most recent assistant message renders them as pills.
  suggestions: string[]
  // Snapshot of the table state as it stood right after this reply, so "Rewind"
  // can restore it. `tableSnapshot` is the active filters (= columns) and
  // `suggestedColumns` the AI-highlighted column uuids at that point.
  tableSnapshot: ActiveFilter[]
  suggestedColumns: string[]
}

// Payloads published by Insights::AgentRunnerJob over MessageBus.
type AgentPayload = {
  conversation_id: number
  kind: 'progress' | 'delta' | 'text' | 'filters' | 'error'
  tool?: string
  text?: string
  message?: string
  explanation?: string
  filters?: unknown[]
  ledger_select?: Record<string, unknown>
  segment?: {
    action: 'selected' | 'saved'
    id: number
    partner_segment_id?: number | null
    name?: string
  }
  sort?: { column: string; direction: string }
  follow_ups?: string[]
}

type ConversationStatus = 'idle' | 'running' | 'failed'

// Shapes returned by the agent conversations API (snake_case, un-camelized).
interface AgentTurnResponse {
  id: number
  message_bus_channel: string
  message_bus_last_id: number
}

interface AgentConversationState {
  status: ConversationStatus
  latest_result: AgentPayload | null
}

interface SuggestedPromptsResponse {
  prompts: string[]
  // Whether a warm job is generating a batch that will arrive as a MessageBus push. False
  // with empty prompts means the backend's failure cooldown: nothing is coming.
  warming: boolean
  message_bus_channel: string
  message_bus_last_id: number
}

// Published by Insights::AgentSuggestedPromptsJob once a batch is generated.
type SuggestedPromptsPayload = {
  prompts: string[]
  locale: string
}

// A single row of the on-screen Insights table, as exposed by the companies store.
interface CompanyRow {
  id: number
  filter_values: Record<string, { formatted_value?: unknown }>
}

export const EMPTY_STATE_SUGGESTIONS_SHOWN = 3

// How long the empty state waits for the warm job to publish a batch before it gives up and
// renders no suggestions at all. Generation is a single Flash call; a batch that has not
// arrived by now is not coming (provider error, or the job died with retry: 0).
export const SUGGESTIONS_PUSH_TIMEOUT_MS = 20_000

// Rows sampled from the on-screen table into the agent's view context.
const CONTEXT_SAMPLE_SIZE = 10

let messageSeq = 0
function nextMessageId(): string {
  messageSeq += 1
  return `assistant-msg-${messageSeq}`
}

export const useAssistantStore = defineStore('insights-assistant', () => {
  const filtersStore = useFiltersStore()

  const conversationId = ref<number | null>(null)
  const messages = ref<AssistantMessage[]>([])
  const draft = ref<string>('')
  const loading = ref<boolean>(false)
  // Cumulative snapshot of the reply being generated, streamed over MessageBus.
  const streamingText = ref<string>('')
  const currentTool = ref<string | null>(null)
  // The 3 prompts currently shown in the empty state (sampled from the pool).
  const emptyStateSuggestions = ref<string[]>([])
  // Full set of firm-tailored prompts last fetched; empty until the first fetch
  // resolves (or when the backend has none and we fall back to the static defaults).
  const suggestionPool = ref<string[]>([])
  // Whether the first fetch has resolved. Until it has, the empty state shows a
  // loading skeleton rather than the static defaults, so the user never sees a
  // placeholder prompt swapped out for the real ones.
  const suggestionsLoaded = ref<boolean>(false)

  // Column uuids the assistant currently proposes; the sf-ai header highlight
  // reads from here.
  const suggestedColumnNames = ref<Set<string>>(new Set())

  // Set on scope teardown. The fetches that trigger subscribing are async, so their
  // resolution can race disposal: without this guard a fetch resolving after teardown would
  // register a handler on the global Polling client that nothing will ever remove.
  let disposed = false

  // One MessageBus subscription: registers at most once, never after disposal, and tears
  // down exactly the handler it registered.
  function createBusSubscription<P>(handler: (payload: P) => void): {
    subscribe: (channel: string, lastId: number) => void
    dispose: () => void
  } {
    let channel: string | null = null

    return {
      subscribe(toChannel: string, lastId: number): void {
        if (disposed || channel !== null || !toChannel) return

        window.Silverfin.Polling.subscribe(toChannel, handler, lastId)
        channel = toChannel
      },
      dispose(): void {
        if (channel === null) return

        window.Silverfin.Polling.unsubscribe(channel, handler)
        channel = null
      },
    }
  }

  // The agent turn channel, subscribed on the first turn and reused across every
  // conversation and turn for the store's lifetime.
  const turnSubscription = createBusSubscription(handleAgentPayload)
  // The suggested-prompts push rides its own channel, subscribed on the first empty-state
  // fetch rather than on the first turn, since prompts are warmed before any conversation.
  const suggestionsSubscription = createBusSubscription(
    handleSuggestedPromptsPayload
  )

  let suggestionsTimeout: number | null = null

  // MessageBus delivery is best-effort: a dropped terminal message would otherwise leave
  // the turn loading forever (the reply and its side effects never arrive). While a turn is
  // loading, this poll reconciles against the authoritative server state and, once the turn
  // is no longer running, concludes it from the persisted latest_result. finishReply's loading
  // guard makes whichever of MessageBus / this poll resolves first win; the other is a no-op.
  const RECONCILE_INTERVAL_MS = 4000
  let reconcileTimer: ReturnType<typeof setInterval> | null = null
  let reconcileInFlight = false

  function stopReconcile(): void {
    if (reconcileTimer) {
      clearInterval(reconcileTimer)
      reconcileTimer = null
    }
  }

  function startReconcile(): void {
    stopReconcile()
    reconcileTimer = setInterval(
      () => void reconcileTurn(),
      RECONCILE_INTERVAL_MS
    )
  }

  async function reconcileTurn(): Promise<void> {
    if (!loading.value || conversationId.value === null || reconcileInFlight)
      return

    // Pin the id we are reconciling: a conversation switch (new chat / open another) during the
    // in-flight fetch must not apply this conversation's result to whatever is now active.
    const reconcilingId = conversationId.value
    reconcileInFlight = true
    try {
      const conv = (await api.fetchAgentConversation(
        reconcilingId
      )) as AgentConversationState
      // A "running" status means the reply is genuinely still in flight; only conclude once
      // the server reports the turn finished and has a result to replay — and only if we are
      // still on the same conversation we fetched.
      if (
        loading.value &&
        conversationId.value === reconcilingId &&
        conv.status !== 'running' &&
        conv.latest_result
      ) {
        handleAgentPayload({
          ...conv.latest_result,
          conversation_id: reconcilingId,
        })
      }
    } catch (error) {
      SfLog.error('Insights assistant reconcile poll failed', { error })
    } finally {
      reconcileInFlight = false
    }
  }

  const hasConversation = computed(() => messages.value.length > 0)

  // Pills only show under the assistant's latest reply (ticket requirement).
  const lastAssistantMessageId = computed(() => {
    for (let i = messages.value.length - 1; i >= 0; i--) {
      if (messages.value[i].role === 'assistant') return messages.value[i].id
    }
    return null
  })

  // The disclaimer under the table is shown once the assistant has proposed at
  // least one table state.
  const tableModifiedByAssistant = computed(() =>
    messages.value.some((message) => message.role === 'assistant')
  )

  function isColumnSuggested(columnName: string): boolean {
    return suggestedColumnNames.value.has(columnName)
  }

  function setDraft(value: string): void {
    draft.value = value
  }

  function addMessage(message: Omit<AssistantMessage, 'id'>): AssistantMessage {
    const entry: AssistantMessage = { id: nextMessageId(), ...message }
    messages.value.push(entry)
    return entry
  }

  // The subscriptions live for the store's lifetime (reused across every conversation and
  // turn), so they are not torn down on cancel/startNew. Release them when the store itself
  // is disposed (page/app teardown) so no dangling MessageBus handler remains — and flag the
  // disposal so an in-flight fetch resolving late cannot re-register one.
  onScopeDispose(() => {
    disposed = true
    stopReconcile()
    clearSuggestionsTimeout()
    turnSubscription.dispose()
    suggestionsSubscription.dispose()
  })

  // Stop / Esc / new chat while pending: the turn's job keeps running server-side,
  // but its payloads are dropped on arrival (see the loading guard in finishReply).
  function cancel(): void {
    loading.value = false
    streamingText.value = ''
    currentTool.value = null
    stopReconcile()
  }

  async function submitPrompt(prompt?: string): Promise<void> {
    const text = (prompt ?? draft.value).trim()
    if (!text || loading.value) return

    addMessage({
      role: 'user',
      text,
      suggestions: [],
      suggestedColumns: [],
      tableSnapshot: [],
    })
    draft.value = ''
    loading.value = true
    streamingText.value = ''
    currentTool.value = null

    try {
      const context = currentViewContext()
      const response = (
        conversationId.value
          ? await api.sendAgentMessage(conversationId.value, text, context)
          : await api.createAgentConversation(text, context)
      ) as AgentTurnResponse
      conversationId.value = response.id
      // Subscribe only after the turn is enqueued, on the channel the server named and from
      // the position it captured before the job could publish (message_bus_last_id, NOT -1:
      // the MessageBus client can register after the job already streamed its updates, and
      // replaying from lastId delivers them regardless of registration timing). Only the
      // first turn subscribes; later turns publish at higher ids the existing subscription
      // already receives.
      turnSubscription.subscribe(
        response.message_bus_channel,
        response.message_bus_last_id ?? 0
      )
      // Safety net in case the terminal MessageBus message is dropped (see reconcileTurn).
      startReconcile()
    } catch (error) {
      SfLog.error('Insights assistant request failed', { error })
      finishReply(I18n.t('companies_overview.assistant.request_failed'))
    }
  }

  // Suggestion pills (both empty-state and reply) submit immediately; we do not
  // wait for a second user action.
  function submitSuggestion(suggestion: string): void {
    void submitPrompt(suggestion)
  }

  // Firm-tailored example prompts for the empty state. Called on each empty-state
  // mount (i.e. each new chat), which also advances the backend's show counter.
  // A returning empty state re-samples the pool it already has, so the pills appear
  // instantly and differ from the previous chat; the fetch below only refreshes the
  // pool for next time.
  //
  // On a cold cache the response carries no prompts: the backend has enqueued a warm job,
  // which publishes the batch over MessageBus when the model returns. The panel keeps its
  // skeleton until that push lands (or the timeout retires it). There are no static prompts
  // to fall back on: a suggestion the firm's data cannot answer is worse than no suggestion.
  async function fetchEmptyStateSuggestions(): Promise<void> {
    const hadPrompts = suggestionPool.value.length > 0
    if (hadPrompts) resampleSuggestions()

    try {
      const response =
        (await api.fetchAgentSuggestedPrompts()) as SuggestedPromptsResponse

      suggestionsSubscription.subscribe(
        response.message_bus_channel,
        response.message_bus_last_id ?? 0
      )

      const prompts = response.prompts ?? []
      if (prompts.length) {
        applySuggestedPrompts(prompts, { resample: !hadPrompts })
      } else if (!hadPrompts) {
        if (response.warming) {
          // Nothing cached but a warm job is generating: wait for its push rather than
          // flash a placeholder, but do not pulse a skeleton forever if it never comes.
          awaitSuggestionsPush()
        } else {
          // An empty batch with no warm in flight is the backend's failure cooldown:
          // nothing is coming, so retire the skeleton now instead of after the timeout.
          suggestionsLoaded.value = true
        }
      }
    } catch (error) {
      SfLog.error('Insights assistant suggested prompts failed', { error })
      suggestionsLoaded.value = true
    }
  }

  // Sets the pool and, on a first load, the three pills sampled from it. Resampling is skipped
  // when the view already shows pills, so a background refresh never reshuffles them mid-view.
  function applySuggestedPrompts(
    prompts: string[],
    { resample }: { resample: boolean }
  ): void {
    suggestionPool.value = prompts
    suggestionsLoaded.value = true
    if (resample) resampleSuggestions()
  }

  function awaitSuggestionsPush(): void {
    clearSuggestionsTimeout()
    suggestionsTimeout = window.setTimeout(() => {
      suggestionsLoaded.value = true
    }, SUGGESTIONS_PUSH_TIMEOUT_MS)
  }

  function clearSuggestionsTimeout(): void {
    if (suggestionsTimeout === null) return

    window.clearTimeout(suggestionsTimeout)
    suggestionsTimeout = null
  }

  // The warm job's batch, pushed when the model returns. A batch generated for a locale the
  // user has since left would render in the wrong language, so it is dropped. Compared by
  // base language, not the full locale string: the prompts vary only by language (EN, NL, FR),
  // and the server's locale ("nl") and the client's ("nl-BE") need not share the region.
  function handleSuggestedPromptsPayload(
    payload: SuggestedPromptsPayload
  ): void {
    if (!payload?.prompts?.length) return
    if (
      payload.locale &&
      baseLanguage(payload.locale) !== baseLanguage(I18n.locale)
    )
      return

    clearSuggestionsTimeout()
    applySuggestedPrompts(payload.prompts, {
      resample: !emptyStateSuggestions.value.length,
    })
  }

  // "nl-BE" -> "nl". Reduces a locale to its language so a regional client locale still
  // matches the language-level locale the server generated the batch for.
  function baseLanguage(locale: string): string {
    return (locale || '').slice(0, 2)
  }

  // Sample the shown prompts from the pool. An empty pool shows no pills at all.
  function resampleSuggestions(): void {
    emptyStateSuggestions.value = sampleSize(
      suggestionPool.value,
      EMPTY_STATE_SUGGESTIONS_SHOWN
    )
  }

  // Snapshot of what the user currently sees, sent with every message so the
  // agent can modify the current filters and reason about on-screen results.
  function currentViewContext(): Record<string, unknown> {
    const rootStore = useInsightsRootStore()
    const segmentsStore = useSegmentsStore()
    const companiesStore = useCompaniesStore()
    const columnsStore = useColumnsStore()

    const activeFilters = filtersStore.getActiveFilters
    const rows = companiesStore.sortedAndFilteredCompanies as CompanyRow[]

    return {
      segment: segmentsStore.segment
        ? {
            id: segmentsStore.segment.id,
            partner_segment_id: segmentsStore.segment.partner_segment_id,
            name: segmentsStore.segment.name,
          }
        : null,
      filters: serialize.filterSettings(
        activeFilters,
        filtersStore.allFiltersByType,
        { includeId: false }
      ),
      ledger_select: serialize.ledgerSelectSettings({
        periodSelection: rootStore.periodSelection,
        ledgerSelectDate: rootStore.ledgerSelectDate,
        ledgerSelectLockType: rootStore.ledgerSelectLockType,
      }),
      sort: {
        column: columnsStore.sortColumn,
        direction: columnsStore.sortType,
      },
      results: {
        total_on_screen: rows.length,
        sample: rows.slice(0, CONTEXT_SAMPLE_SIZE).map((row) => ({
          company_id: row.id,
          name: row.filter_values?.name?.formatted_value,
          values: Object.fromEntries(
            activeFilters.map((filter) => [
              filter.uuid,
              row.filter_values?.[filter.uuid]?.formatted_value,
            ])
          ),
        })),
      },
    }
  }

  function handleAgentPayload(payload: AgentPayload): void {
    if (payload.conversation_id !== conversationId.value) return

    switch (payload.kind) {
      case 'progress':
        if (payload.tool) currentTool.value = payload.tool
        break
      case 'delta':
        // Cumulative snapshot of the answer being generated
        streamingText.value = payload.text ?? ''
        break
      case 'text':
        void concludeTurn(payload, payload.text ?? '')
        break
      case 'filters':
        void concludeTurn(payload, payload.explanation ?? '')
        break
      case 'error':
        finishReply(
          payload.message ??
            I18n.t('companies_overview.assistant.request_failed')
        )
        break
      default:
        // The backend should only send the kinds handled above; surface anything else so a
        // malformed payload is visible instead of being silently dropped.
        SfLog.warn('Insights assistant received an unknown payload kind', {
          extra: { payload },
        })
    }
  }

  // Side effects must land before the reply is appended, so the reply's table
  // snapshot (used by Rewind) reflects the state the assistant produced.
  async function concludeTurn(
    payload: AgentPayload,
    text: string
  ): Promise<void> {
    // A terminal payload that arrives after the turn already ended (cancelled, or a
    // MessageBus message that lost the race to the reconcile poll) must not re-apply side
    // effects or append a duplicate reply. Claim the turn synchronously here, BEFORE the
    // applySideEffects await, so a second concludeTurn racing during that await sees
    // loading=false and bails — otherwise both would apply side effects.
    if (!loading.value) return
    loading.value = false

    // Side effects (segment load, filter run) can fail independently of the reply itself;
    // still surface the answer rather than dropping it silently, and record why the table
    // may not reflect what the reply describes.
    try {
      await applySideEffects(payload)
    } catch (error) {
      SfLog.error('Insights assistant failed to apply turn side effects', {
        error,
      })
    }
    appendReply(text, payload)
  }

  // Segment selection/saving loads the segment (which resets filters), so it must
  // complete before agent-provided filters are applied on top.
  async function applySideEffects(payload: AgentPayload): Promise<void> {
    const segmentsStore = useSegmentsStore()

    if (payload.segment) {
      if (payload.segment.action === 'saved')
        await segmentsStore.fetchSegments()
      await segmentsStore.loadSegment({
        id: payload.segment.id,
        partner_segment_id: payload.segment.partner_segment_id ?? undefined,
      })
    }

    if (payload.kind === 'filters' || payload.filters?.length) {
      applyAgentFilters(payload)
    }

    if (payload.sort) {
      useColumnsStore().setSortAndType({
        sortColumn: payload.sort.column,
        sortType: payload.sort.direction,
      })
    }
  }

  function applyAgentFilters(payload: AgentPayload): void {
    const rootStore = useInsightsRootStore()
    const segmentsStore = useSegmentsStore()
    const ledgerSelect = payload.ledger_select ?? {}

    // Agent-built filters are an ad-hoc query: detach from the selected segment,
    // otherwise its last request_ref serves the cached run computed with the OLD
    // filters and new columns never receive data (and the fresh run would be
    // persisted onto the saved segment).
    if (segmentsStore.segment) segmentsStore.unselectSegment()

    Object.values(periodSelectionTypes).forEach((periodType) => {
      if (ledgerSelect[periodType]) rootStore.setPeriodSelection(periodType)
    })
    if (typeof ledgerSelect.date === 'string') {
      rootStore.setLedgerSelectDate(ledgerSelect.date)
    }
    if (typeof ledgerSelect.locking_type === 'string') {
      rootStore.setLedgerSelectLockType(ledgerSelect.locking_type)
    }

    filtersStore.setActiveFilters(payload.filters ?? [])
    // The whole table state is assistant-built at this point: highlight every
    // applied column (setActiveFilters keeps the agent's cached_uuid as uuid).
    suggestedColumnNames.value = new Set(
      filtersStore.getActiveFilters.map((filter) => filter.uuid)
    )
    void useCompaniesStore().fetchFilteredCompanies()
  }

  // Ends the loading state and appends the reply. A turn that already ended (cancelled via
  // Stop / Esc, or concluded by the racing MessageBus / reconcile path) has loading off, so
  // its late payload is dropped. concludeTurn claims the turn itself and calls appendReply
  // directly, so this guarded entry point is for the error/failure paths.
  function finishReply(text: string, payload?: AgentPayload): void {
    if (!loading.value) return

    loading.value = false
    appendReply(text, payload)
  }

  // Appends the assistant reply and clears the per-turn UI state. Assumes the caller has
  // already claimed the turn (set loading false); never guards on loading itself.
  function appendReply(text: string, payload?: AgentPayload): void {
    addMessage({
      role: 'assistant',
      text,
      suggestions: payload?.follow_ups ?? [],
      // Capture the table state as it stands now so Rewind can restore it.
      tableSnapshot: cloneDeep(filtersStore.getActiveFilters),
      suggestedColumns: [...suggestedColumnNames.value],
    })
    streamingText.value = ''
    currentTool.value = null
    stopReconcile()
  }

  // Rewinds the table to the state reflected in the given assistant reply:
  // restores the columns (active filters) and the AI highlight from its
  // snapshot. The filters watcher then re-runs the query for that state.
  function rewindTo(messageId: string): void {
    const target = messages.value.find((message) => message.id === messageId)
    if (!target || target.role !== 'assistant') return

    filtersStore.restoreActiveFilters(target.tableSnapshot)
    suggestedColumnNames.value = new Set(target.suggestedColumns)
  }

  // Drops the panel state; the next message starts a new conversation. Late
  // payloads for the old conversation no longer match conversationId and are
  // ignored. The table's columns stay; only the AI highlight is cleared.
  function startNew(): void {
    cancel()
    conversationId.value = null
    messages.value = []
    draft.value = ''
    suggestedColumnNames.value = new Set()
  }

  return {
    conversationId,
    messages,
    draft,
    loading,
    streamingText,
    currentTool,
    emptyStateSuggestions,
    suggestionsLoaded,
    hasConversation,
    lastAssistantMessageId,
    tableModifiedByAssistant,
    isColumnSuggested,
    setDraft,
    submitPrompt,
    submitSuggestion,
    fetchEmptyStateSuggestions,
    rewindTo,
    cancel,
    startNew,
  }
})
